import React, { useCallback, useEffect, useRef, useState } from "react";
import useInbox from "../../InboxTab/InboxContext";

export default function EmailBodyHeader({ message }) {
  const { messageId, formatGmailDate, getSenderName } = useInbox();

  /* ******************** All States ************************* */
  const [showEmailDetails, setShowEmailDetails] = useState(false);
  const dropdownRef = useRef(null);

  /* ******************** All Functions ************************* */

  const formatEmailAddress = useCallback((emailObj) => {
    if (!emailObj) return "";
    const name = emailObj.name || "";
    const email = emailObj.email || "";
    if (name && email) {
      return `${name} <${email}>`;
    }
    return email || name;
  }, []);

  const formatEmailWithBoldName = useCallback((emailObj) => {
    if (!emailObj) return null;
    const name = emailObj.name || "";
    const email = emailObj.email || "";
    if (name && email) {
      return (
        <>
          <strong>{name}</strong> &lt;{email}&gt;
        </>
      );
    }
    return email || name;
  }, []);

  const formatEmailListWithBoldNames = useCallback(
    (emailArray) => {
      if (!emailArray || !Array.isArray(emailArray) || emailArray.length === 0)
        return null;
      return emailArray.map((e, index) => (
        <span key={index}>
          {index > 0 && ", "}
          {formatEmailWithBoldName(e)}
        </span>
      ));
    },
    [formatEmailWithBoldName],
  );

  const formatEmailList = useCallback(
    (emailArray) => {
      if (!emailArray || !Array.isArray(emailArray) || emailArray.length === 0)
        return "";
      return emailArray.map((e) => formatEmailAddress(e)).join(", ");
    },
    [formatEmailAddress],
  );

  const formatDateTime = useCallback((dateString) => {
    if (!dateString) return "";
    try {
      // Convert Unix timestamp (seconds) to milliseconds
      const timestamp =
        typeof dateString === "number" ? dateString * 1000 : dateString;
      const date = new Date(timestamp);
      const options = {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      };
      return date.toLocaleString("en-US", options);
    } catch (error) {
      return dateString;
    }
  }, []);

  /* ******************** All UseEffects ************************* */

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowEmailDetails(false);
      }
    };

    if (showEmailDetails) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showEmailDetails]);

  return (
    <div className="flex-grow-1 w-100">
      {/* Gmail-style Email Details Header */}
      <div
        className="email-details-header-wrapper mb-2 position-relative"
        ref={dropdownRef}
      >
        <div className="d-flex align-items-start justify-content-between">
          <div className="">
            <div
              className="d-flex align-items-center gap-2"
              onClick={() => setShowEmailDetails(!showEmailDetails)}
            >
              <span className="fs-5 fw-bold text-dark">
                {getSenderName(message, message?.grantEmail)?.toUpperCase()}
              </span>
              <button
                className="btn btn-link p-0 text-muted"
                title="Show details"
                style={{ fontSize: "0.875rem" }}
              >
                <i
                  className={`mdi mdi-chevron-${
                    showEmailDetails ? "up" : "down"
                  }`}
                ></i>
              </button>
            </div>
            <div className="d-flex align-items-center gap-2 text-muted small">
              <span>
                to {message?.to?.[0]?.name || message?.to?.[0]?.email || "me"}
              </span>
              {message?.to && message.to.length > 1 && (
                <span className="badge bg-secondary">
                  +{message.to.length - 1}
                </span>
              )}
            </div>

            {/* Expanded Details - Dropdown Style */}
            {showEmailDetails && (
              <div className="email-details-expanded">
                <table className="table table-sm table-borderless mb-0">
                  <tbody>
                    <tr>
                      <td className="text-muted fw-semibold">from:</td>
                      <td className="text-dark">
                        {formatEmailListWithBoldNames(message?.from) ||
                          message?.fromName ||
                          getSenderName(message, message?.grantEmail) ||
                          "Unknown"}
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted fw-semibold">to:</td>
                      <td className="text-dark">
                        {formatEmailListWithBoldNames(message?.to) || ""}
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted fw-semibold">reply-to:</td>
                      <td className="text-dark">
                        {formatEmailListWithBoldNames(message?.replyTo) || ""}
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted fw-semibold">cc:</td>
                      <td className="text-dark">
                        {formatEmailListWithBoldNames(message?.cc) || ""}
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted fw-semibold">bcc:</td>
                      <td className="text-dark">
                        {formatEmailListWithBoldNames(message?.bcc) || ""}
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted fw-semibold">date:</td>
                      <td className="text-dark">
                        {formatDateTime(message?.date) || "N/A"}
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted fw-semibold">subject:</td>
                      <td className="text-dark">
                        {message?.subject || "(No Subject)"}
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted fw-semibold">mailed-by:</td>
                      <td className="text-dark">
                        {message?.grantEmail ||
                          message?.companyEmail ||
                          "gmail.com"}
                      </td>
                    </tr>
                    {message?.messageId && (
                      <tr>
                        <td className="text-muted fw-semibold">message-id:</td>
                        <td
                          className="text-dark text-break"
                          style={{ fontSize: "0.875rem" }}
                        >
                          {message.messageId}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div
            className="h6 bg-white px-2 py-1 my-0 rounded-3 shadow-sm"
            style={{
              paddingTop: 2,
              paddingBottom: 2,
            }}
          >
            {formatGmailDate(message?.date)}
          </div>
        </div>
      </div>

      <style>
        {`
          .email-details-header-wrapper {
            background-color: transparent;
          }
          .email-details-expanded {
            position: absolute;
            top: 100%;
            left: -30px;
            z-index: 1000;
            background: white;
            border: 1px solid #e2e8f0;
            border-radius: 8px;
            padding: 0.75rem;
            margin-top: 0.5rem;
            box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
            animation: slideDown 0.2s ease-out;
            width: fit-content;
            min-width: 400px;
            max-width: 600px;
          }
          @keyframes slideDown {
            from {
              opacity: 0;
              transform: translateY(-10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          .email-details-expanded table td {
            padding: 0.15rem 0.5rem;
            vertical-align: top;
            word-break: break-word;
            font-size: 0.875rem;
          }
          .email-details-expanded table td:first-child {
            white-space: nowrap;
            padding-right: 1rem;
          }
        `}
      </style>
    </div>
  );
}
